import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Grid, Box, Card, CardContent, Typography, TextField, Select, MenuItem, Chip, IconButton } from '@mui/material';
import FavoriteIcon from '@mui/icons-material/Favorite';
import FavoriteBorderIcon from '@mui/icons-material/FavoriteBorder';
import SearchRoundedIcon from '@mui/icons-material/SearchRounded';
import { useGetProfilesQuery } from '../store/docApi';
import { isArrayOfStrings } from '../utils';

export default function Explore() {
  const navigate = useNavigate();
  const { data: profiles } = useGetProfilesQuery();
  const [filteredProfiles, setFilteredProfiles] = useState([]);
  const [favoriteProfiles, setFavoriteProfiles] = useState([]);
  const [savedFavorites, setSavedFavorites] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedTag, setSelectedTag] = useState('All');

  const tags = ['All', 'Knitting', 'Crochet', 'Motifs'];

  useEffect(() => {
    if (profiles) {
      const _filteredProfiles = profiles.filter((profile) => {
        const matchesSearch =
          (profile.name && profile.name.toLowerCase().includes(searchQuery.toLowerCase())) ||
          (profile.owner && profile.owner.toLowerCase().includes(searchQuery.toLowerCase())) ||
          (profile.tags && profile.tags.some((tag) => tag.toLowerCase().includes(searchQuery.toLowerCase())));
        const matchesTags = selectedTag === 'All' || (profile.tags && profile.tags.includes(selectedTag));
        return matchesSearch && matchesTags;
      });
      setFilteredProfiles(_filteredProfiles);

      if (savedFavorites) {
        const _favoriteProfiles = profiles.filter((profile) => savedFavorites.includes(profile.id));
        setFavoriteProfiles(_favoriteProfiles);
      }
    }
  }, [profiles, searchQuery, selectedTag, savedFavorites]);

  useEffect(() => {
    try {
      const _savedFavs = JSON.parse(localStorage.getItem('favoriteProfiles'));
      setSavedFavorites(isArrayOfStrings(_savedFavs) ? _savedFavs : []);
    } catch (error) {
      console.log('Error loading saved favorites!');
    }
  }, []);

  useEffect(() => {
    localStorage.setItem('favoriteProfiles', JSON.stringify(savedFavorites));
  }, [savedFavorites]);

  const toggleFavorite = (profileId) => {
    setSavedFavorites((prev) => (prev.includes(profileId) ? prev.filter((id) => id !== profileId) : [...prev, profileId]));
  };

  const ProfileCard = ({ profile, isFavorite, onFavoriteToggle }) => {
    return (
      <Card sx={{ width: 220, m: 1, cursor: 'pointer' }} onClick={() => navigate(`/explore/${profile.id}`)}>
        {profile.picture ? (
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              height: 140
            }}
          >
            <img src={profile.picture} alt={profile.name} width="140" />
          </Box>
        ) : (
          <Box
            sx={{
              height: 140,
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              backgroundColor: 'grey.200'
            }}
          >
            <Typography color="text.secondary">No Image</Typography>
          </Box>
        )}
        <CardContent>
          <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
            {profile.name}
          </Typography>
          <Box
            sx={{
              display: 'flex',
              gap: 1,
              flexWrap: 'wrap',
              mt: 1,
              justifyContent: 'center'
            }}
          >
            {profile.tags && profile.tags.map((tag) => <Chip key={tag} size="small" variant="outlined" label={tag} />)}
          </Box>
          <IconButton
            onClick={(e) => {
              e.stopPropagation();
              onFavoriteToggle();
            }}
            color={isFavorite ? 'error' : 'default'}
          >
            {isFavorite ? <FavoriteIcon /> : <FavoriteBorderIcon />}
          </IconButton>
        </CardContent>
      </Card>
    );
  };

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center'
      }}
    >
      <Typography variant="h4" gutterBottom>
        Explore
      </Typography>
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'row',
          gap: 1.5,
          alignItems: 'center',
          flexWrap: 'wrap',
          justifyContent: 'center'
        }}
      >
        <TextField
          size="small"
          variant="outlined"
          placeholder="Search…"
          onChange={(e) => setSearchQuery(e.target.value)}
          InputProps={{
            startAdornment: <SearchRoundedIcon color="primary" />
          }}
          sx={{
            alignSelf: 'center',
            flexGrow: 1,
            minWidth: 100,
            maxWidth: 150
          }}
        />
        <Select
          value={selectedTag}
          onChange={(e) => setSelectedTag(e.target.value)}
          size="small"
          sx={{ flexGrow: 1, minWidth: 100, maxWidth: 100 }}
        >
          {tags.map((tag) => (
            <MenuItem key={tag} value={tag}>
              {tag}
            </MenuItem>
          ))}
        </Select>
      </Box>
      {favoriteProfiles && favoriteProfiles.length > 0 && (
        <Box sx={{ width: '100%', textAlign: 'center' }}>
          <Typography gutterBottom sx={{ mt: 2 }}>
            Favorites
          </Typography>
          <Grid container spacing={2} sx={{ flexGrow: 1, justifyContent: 'center' }}>
            {favoriteProfiles.map((profile, i) => (
              <ProfileCard
                key={i}
                profile={profile}
                isFavorite={savedFavorites.includes(profile.id)}
                onFavoriteToggle={() => toggleFavorite(profile.id)}
              />
            ))}
          </Grid>
        </Box>
      )}
      <Box sx={{ width: '100%', textAlign: 'center' }}>
        <Typography gutterBottom sx={{ mt: 2 }}>
          All
        </Typography>
        <Grid container spacing={2} sx={{ flexGrow: 1, justifyContent: 'center' }}>
          {filteredProfiles.map((profile, i) => (
            <ProfileCard
              key={i}
              profile={profile}
              isFavorite={savedFavorites.includes(profile.id)}
              onFavoriteToggle={() => toggleFavorite(profile.id)}
            />
          ))}
        </Grid>
      </Box>
    </Box>
  );
}
